import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import apiClient from "../../services/apiClient";
import { useAuth } from "../../services/AuthContext";

const STEPS = ["Entité", "Admin", "Outlet", "Register"];

const SuperadminEntityOnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth() as any;

  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [company, setCompany] = useState<any | null>(null);
  const [outlets, setOutlets] = useState<any[]>([]);

  const [entityForm, setEntityForm] = useState({
    siret: "",
    legal_name: "",
    trade_name: "",
    base_currency: "EUR",
    address_street: "",
    address_postal_code: "",
    address_city: "",
    address_country: "",
  });

  const [adminForm, setAdminForm] = useState({
    email: "",
    password: "",
    display_name: "",
  });

  const [outletForm, setOutletForm] = useState({
    name: "",
    base_currency: "EUR",
    address_street: "",
    address_postal_code: "",
    address_city: "",
    address_country: "",
  });

  const [registerForm, setRegisterForm] = useState({
    outlet_id: "",
    name: "",
  });

  const isSuperadmin = useMemo(
    () => Array.isArray(user?.roles) && user.roles.includes("superadmin"),
    [user?.roles],
  );

  const handleCreateEntity = async () => {
    if (!entityForm.siret || !entityForm.legal_name || !entityForm.base_currency) {
      setError("SIRET, raison sociale et devise sont obligatoires.");
      return;
    }

    try {
      setError(null);
      const response = await apiClient.post("/api/companies", {
        siret: entityForm.siret.trim(),
        legal_name: entityForm.legal_name.trim(),
        trade_name: entityForm.trade_name.trim() || null,
        base_currency: entityForm.base_currency.trim().toUpperCase(),
        address: {
          street: entityForm.address_street.trim() || null,
          postal_code: entityForm.address_postal_code.trim() || null,
          city: entityForm.address_city.trim() || null,
          country: entityForm.address_country.trim() || null,
        },
      });
      setCompany(response.company);
      setStep(1);
    } catch (e: any) {
      setError(e?.message || "Impossible de créer l'entité.");
    }
  };

  const handleCreateAdmin = async () => {
    if (!company?.id) return;
    if (!adminForm.email || !adminForm.password || !adminForm.display_name) {
      setError("Email, mot de passe et nom sont obligatoires.");
      return;
    }

    try {
      setError(null);
      await apiClient.post(`/api/companies/${company.id}/users`, {
        email: adminForm.email.trim(),
        password: adminForm.password,
        display_name: adminForm.display_name.trim(),
        roles: ["admin"],
      });
      setStep(2);
    } catch (e: any) {
      setError(e?.message || "Impossible de créer l'admin.");
    }
  };

  const handleCreateOutlet = async () => {
    if (!company?.id) return;
    if (!outletForm.name) {
      setError("Le nom de l'outlet est obligatoire.");
      return;
    }

    try {
      setError(null);
      const response = await apiClient.post("/api/outlets", {
        company_id: company.id,
        name: outletForm.name.trim(),
        base_currency: outletForm.base_currency.trim().toUpperCase(),
        address: {
          street: outletForm.address_street.trim() || null,
          postal_code: outletForm.address_postal_code.trim() || null,
          city: outletForm.address_city.trim() || null,
          country: outletForm.address_country.trim() || null,
        },
      });
      const createdOutlet = response.outlet;
      const nextOutlets = [...outlets, createdOutlet];
      setOutlets(nextOutlets);
      setRegisterForm((prev) => ({
        ...prev,
        outlet_id: prev.outlet_id || createdOutlet.id,
      }));
      setStep(3);
    } catch (e: any) {
      setError(e?.message || "Impossible de créer l'outlet.");
    }
  };

  const handleCreateRegister = async () => {
    if (!company?.id) return;
    if (!registerForm.outlet_id || !registerForm.name) {
      setError("Outlet et nom du register sont obligatoires.");
      return;
    }

    try {
      setError(null);
      await apiClient.post("/api/registers", {
        company_id: company.id,
        outlet_id: registerForm.outlet_id,
        name: registerForm.name.trim(),
        is_active: true,
      });
      navigate("/superadmin/entities");
    } catch (e: any) {
      setError(e?.message || "Impossible de créer le register.");
    }
  };

  if (!isSuperadmin) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Accès réservé au superadmin.</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
        Création d'entité
      </Typography>

      <Stepper activeStep={step} sx={{ mb: 3 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: 3 }}>
        {step === 0 ? (
          <Box sx={{ display: "grid", gap: 2 }}>
            <TextField
              label="SIRET"
              required
              value={entityForm.siret}
              onChange={(e) => setEntityForm({ ...entityForm, siret: e.target.value })}
            />
            <TextField
              label="Raison sociale"
              required
              value={entityForm.legal_name}
              onChange={(e) =>
                setEntityForm({ ...entityForm, legal_name: e.target.value })
              }
            />
            <TextField
              label="Nom commercial"
              value={entityForm.trade_name}
              onChange={(e) =>
                setEntityForm({ ...entityForm, trade_name: e.target.value })
              }
            />
            <TextField
              label="Devise"
              required
              value={entityForm.base_currency}
              onChange={(e) =>
                setEntityForm({ ...entityForm, base_currency: e.target.value })
              }
            />
            <TextField
              label="Rue"
              value={entityForm.address_street}
              onChange={(e) =>
                setEntityForm({ ...entityForm, address_street: e.target.value })
              }
            />
            <TextField
              label="Code postal"
              value={entityForm.address_postal_code}
              onChange={(e) =>
                setEntityForm({ ...entityForm, address_postal_code: e.target.value })
              }
            />
            <TextField
              label="Ville"
              value={entityForm.address_city}
              onChange={(e) =>
                setEntityForm({ ...entityForm, address_city: e.target.value })
              }
            />
            <TextField
              label="Pays"
              value={entityForm.address_country}
              onChange={(e) =>
                setEntityForm({ ...entityForm, address_country: e.target.value })
              }
            />
            <Box>
              <Button variant="contained" onClick={handleCreateEntity}>
                Créer l'entité et continuer
              </Button>
            </Box>
          </Box>
        ) : null}

        {step === 1 ? (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Typography variant="subtitle1">
              Entité créée: {company?.trade_name || company?.legal_name}
            </Typography>
            <TextField
              label="Nom admin"
              required
              value={adminForm.display_name}
              onChange={(e) =>
                setAdminForm({ ...adminForm, display_name: e.target.value })
              }
            />
            <TextField
              label="Email admin"
              type="email"
              required
              value={adminForm.email}
              onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
            />
            <TextField
              label="Mot de passe"
              type="password"
              required
              value={adminForm.password}
              onChange={(e) =>
                setAdminForm({ ...adminForm, password: e.target.value })
              }
            />
            <Box>
              <Button variant="contained" onClick={handleCreateAdmin}>
                Créer l'admin et continuer
              </Button>
            </Box>
          </Box>
        ) : null}

        {step === 2 ? (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Typography variant="subtitle1">Créer le premier outlet</Typography>
            <TextField
              label="Nom outlet"
              required
              value={outletForm.name}
              onChange={(e) => setOutletForm({ ...outletForm, name: e.target.value })}
            />
            <TextField
              label="Devise"
              value={outletForm.base_currency}
              onChange={(e) =>
                setOutletForm({ ...outletForm, base_currency: e.target.value })
              }
            />
            <TextField
              label="Rue"
              value={outletForm.address_street}
              onChange={(e) =>
                setOutletForm({ ...outletForm, address_street: e.target.value })
              }
            />
            <TextField
              label="Code postal"
              value={outletForm.address_postal_code}
              onChange={(e) =>
                setOutletForm({ ...outletForm, address_postal_code: e.target.value })
              }
            />
            <TextField
              label="Ville"
              value={outletForm.address_city}
              onChange={(e) =>
                setOutletForm({ ...outletForm, address_city: e.target.value })
              }
            />
            <TextField
              label="Pays"
              value={outletForm.address_country}
              onChange={(e) =>
                setOutletForm({ ...outletForm, address_country: e.target.value })
              }
            />
            <Box>
              <Button variant="contained" onClick={handleCreateOutlet}>
                Créer l'outlet et continuer
              </Button>
            </Box>
          </Box>
        ) : null}

        {step === 3 ? (
          <Box sx={{ display: "grid", gap: 2 }}>
            <Typography variant="subtitle1">Créer le premier register</Typography>
            <TextField
              label="Outlet"
              select
              SelectProps={{ native: true }}
              value={registerForm.outlet_id}
              onChange={(e) =>
                setRegisterForm({ ...registerForm, outlet_id: e.target.value })
              }>
              <option value="">Sélectionner un outlet</option>
              {outlets.map((outlet) => (
                <option key={outlet.id} value={outlet.id}>
                  {outlet.name}
                </option>
              ))}
            </TextField>
            <TextField
              label="Nom register"
              required
              value={registerForm.name}
              onChange={(e) =>
                setRegisterForm({ ...registerForm, name: e.target.value })
              }
            />
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="contained" onClick={handleCreateRegister}>
                Créer le register et terminer
              </Button>
              <Button variant="outlined" onClick={() => navigate("/superadmin/entities")}>
                Terminer plus tard
              </Button>
            </Box>
          </Box>
        ) : null}
      </Paper>
    </Container>
  );
};

export default SuperadminEntityOnboardingPage;
